import React, { useState, useRef, useContext, useEffect, useCallback } from "react";
import IconButton from "@material-ui/core/IconButton";
import ChatIcon from "@material-ui/icons/Chat";
import NotificationsIcon from "@material-ui/icons/Notifications";
import WhatsAppIcon from "@material-ui/icons/WhatsApp";
import Badge from "@material-ui/core/Badge";
import Popover from "@material-ui/core/Popover";
import Typography from "@material-ui/core/Typography";
import List from "@material-ui/core/List";
import ListItem from "@material-ui/core/ListItem";
import CheckIcon from "@material-ui/icons/Check";
import CloseIcon from "@material-ui/icons/Close";
import Tooltip from "@material-ui/core/Tooltip";
import { useTheme } from "@material-ui/core/styles";
import notifySound from "../../assets/chat_notify.mp3";
import { SocketContext } from "../../context/Socket/SocketContext";
import { AuthContext } from "../../context/Auth/AuthContext";
import api from "../../services/api";

const NotificationsPopOver = ({ volume: volumeProp, customStyle }) => {
	const theme = useTheme();
	const { user } = useContext(AuthContext);
	
	// ✅ SOLO LOG UNA VEZ PARA EVITAR SPAM
	if (!NotificationsPopOver._logged) {
		NotificationsPopOver._logged = true;
	}

	// ✅ INICIALIZAR COMPONENTE INMEDIATAMENTE
	useEffect(() => {
		componentInitializedRef.current = true;
		
		// ✅ Cargar URL del favicon dinámicamente desde la API
		const loadFaviconUrl = async () => {
			try {
				const logosResponse = await api.get('/settings/logos');
				if (logosResponse.data?.success && logosResponse.data?.logos) {
					const faviconLogo = logosResponse.data.logos.find(logo => logo.type === 'favicon');
					if (faviconLogo && faviconLogo.url) {
						setFaviconUrl(faviconLogo.url);
					}
				}
			} catch (error) {
				// Si falla, mantener el favicon.ico estático como fallback
				console.warn('Error cargando favicon para notificaciones:', error);
			}
		};
		
		loadFaviconUrl();
		
		// ✅ CLEANUP AL DESMONTAR COMPONENTE
		return () => {
			isMountedRef.current = false;
			componentInitializedRef.current = false;
		};
	}, []);

	const [anchorEl, setAnchorEl] = useState(null);
	// ✅ PERSISTENCIA DE NOTIFICACIONES CON BASE DE DATOS
	const [notifications, setNotifications] = useState([]);
	const [notificationCount, setNotificationCount] = useState(0);
	// ✅ URL del favicon dinámico (fallback a favicon.ico estático)
	const [faviconUrl, setFaviconUrl] = useState("/favicon.ico");
	
	// ✅ CORRECCIÓN #2: Leer volumen desde prop o localStorage (fallback a 0.5)
	const volume = volumeProp !== undefined 
		? volumeProp 
		: Number(localStorage.getItem("volume")) || 0.5;
	
	// ✅ CORRECCIÓN ROBUSTA: Usar Audio nativo para control dinámico del volumen
	// new Audio() permite cambiar el volumen en tiempo real, a diferencia de useSound
	const audioRef = useRef(null);
	const volumeRef = useRef(volume);
	
	// ✅ CORRECCIÓN ROBUSTA: Inicializar Audio una sola vez
	useEffect(() => {
		if (!audioRef.current) {
			audioRef.current = new Audio(notifySound);
			audioRef.current.preload = 'auto';
			audioRef.current.volume = volume;
			volumeRef.current = volume;
		}
		
		return () => {
			// Cleanup: pausar y limpiar audio al desmontar
			if (audioRef.current) {
				audioRef.current.pause();
				audioRef.current = null;
			}
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []); // Solo inicializar una vez - volume se actualiza en el siguiente useEffect
	
	// ✅ CORRECCIÓN ROBUSTA: Actualizar volumen dinámicamente cuando cambie
	useEffect(() => {
		if (audioRef.current) {
			audioRef.current.volume = volume;
			volumeRef.current = volume;
		}
	}, [volume]);
	
	// ✅ CORRECCIÓN ROBUSTA: Función para reproducir sonido con volumen actual
	const playSound = useCallback(() => {
		try {
			if (audioRef.current) {
				// Asegurar que el volumen esté actualizado
				audioRef.current.volume = volumeRef.current;
				// Reiniciar desde el inicio si ya estaba reproduciendo
				audioRef.current.currentTime = 0;
				// Reproducir
				audioRef.current.play().catch(error => {
					// Silenciar errores de autoplay (requiere interacción del usuario)
					if (error.name !== 'NotAllowedError') {
						console.error("🔔 ERROR AL REPRODUCIR SONIDO:", error);
					}
				});
			}
		} catch (error) {
			console.error("🔔 ERROR AL REPRODUCIR SONIDO:", error);
		}
	}, []);
	
	const soundAlertRef = useRef(playSound);
	
	// ✅ CORRECCIÓN ROBUSTA: Actualizar referencia cuando cambie playSound
	useEffect(() => {
		soundAlertRef.current = playSound;
	}, [playSound]);
	const isMountedRef = useRef(true);
	const componentInitializedRef = useRef(false);
	const socketInitializedRef = useRef(false);

	// ✅ OBTENER COMPANY ID DESDE LOCALSTORAGE
	// eslint-disable-next-line no-unused-vars
	const companyId = localStorage.getItem("companyId");
	// console.log("🔔 NotificationsPopOver: companyId obtenido:", companyId); // ✅ REDUCIDO SPAM

	// ✅ FUNCIÓN HELPER PARA CONSTRUIR URLS DINÁMICAS BASADAS EN CONFIGURACIÓN REAL
	const getBackendUrl = useCallback(() => {
		// ✅ PRIORIDAD 1: Variable de entorno del frontend (ya configurada correctamente)
		const backendUrl = process.env.REACT_APP_BACKEND_URL || window.location.origin;
		
		if (backendUrl) {
			return backendUrl;
		}
		
		// ✅ PRIORIDAD 2: Construir desde variables de entorno del sistema
		// const frontendPort = (typeof process !== 'undefined' && process.env) ? process.env.PORT : '3000';
		const backendPort = (typeof process !== 'undefined' && process.env) ? process.env.REACT_APP_BACKEND_PORT : '8080'; // Puerto del backend
		
		const origin = window.location.origin;
		const protocol = origin.split('://')[0];
		const hostname = origin.split('://')[1].split(':')[0];
		
		const dynamicUrl = `${protocol}://${hostname}:${backendPort}`;
		return dynamicUrl;
	}, []);

	// ✅ FUNCIONES DE API PARA NOTIFICACIONES - USANDO CAMPOS EXISTENTES
	const fetchNotifications = useCallback(async () => {
		try {
			// ✅ BUSCAR TICKETS CON MENSAJES SIN LEER EN TODOS LOS STATUS
			const response = await fetch(`${getBackendUrl()}/tickets?pageNumber=1&showAll=true&withUnreadMessages=true`, {
				method: 'GET',
				headers: {
					'Authorization': `Bearer ${localStorage.getItem('token')}`,
					'Content-Type': 'application/json'
				}
			});
			
			if (response.ok) {
				const contentType = response.headers.get('content-type');
				if (!contentType || !contentType.includes('application/json')) {
					console.error("❌ RESPUESTA NO ES JSON:", contentType);
					const text = await response.text();
					console.error("❌ CONTENIDO RECIBIDO:", text.substring(0, 200));
					return;
				}
				
				const data = await response.json();
				let ticketsWithUnread = data.tickets?.filter(ticket => ticket.unreadMessages > 0) || [];
				
				// ✅ FILTRAR POR USUARIO: Si el usuario no es admin, solo mostrar tickets asignados a él
				if (user?.profile !== 'admin') {
					ticketsWithUnread = ticketsWithUnread.filter(ticket => ticket.userId === user?.id);
				}
				
				// ✅ CONVERTIR TICKETS A FORMATO DE NOTIFICACIONES
				const notifications = ticketsWithUnread.map(ticket => ({
					id: `ticket-${ticket.id}`,
					ticketId: ticket.id,
					contactId: ticket.contactId,
					message: ticket.lastMessage || 'Nuevo mensaje',
					ticketStatus: ticket.status,
					queueName: ticket.queue?.name || 'Sin departamento',
					tags: ticket.tags || [],
					contactName: ticket.contact?.name || 'Contacto',
					contactAvatar: ticket.contact?.avatar || null,
					timestamp: ticket.updatedAt,
					metadata: {
						ticketUuid: ticket.uuid,
						contactName: ticket.contact?.name || 'Contacto',
						contactAvatar: ticket.contact?.avatar || null
					},
					createdAt: ticket.updatedAt,
					isRead: ticket.unreadMessages === 0
				}));
				
				// ✅ VERIFICAR QUE EL COMPONENTE SIGA MONTADO ANTES DE ACTUALIZAR ESTADO
				if (isMountedRef.current) {
					setNotifications(notifications);
					// ✅ SINCRONIZAR CONTADOR CON NOTIFICACIONES NO LEÍDAS
					const unreadCount = notifications.filter(n => !n.isRead).length;
					setNotificationCount(unreadCount);
				}
			} else {
				console.error("❌ ERROR AL CARGAR TICKETS:", response.status, response.statusText);
				const text = await response.text();
				console.error("❌ CONTENIDO DE ERROR:", text.substring(0, 200));
			}
		} catch (error) {
			console.error("❌ ERROR AL CARGAR TICKETS:", error);
		}
	}, [getBackendUrl, user]);

	const markNotificationAsRead = useCallback(async (notificationId) => {
		try {
			// ✅ USAR API EXISTENTE PARA MARCAR MENSAJES COMO LEÍDOS
			const ticketId = notificationId.replace('ticket-', '');
			

			// ✅ NO ACTUALIZAR ESTADO LOCAL INMEDIATAMENTE - ESPERAR RESPUESTA DE API
			const response = await fetch(`${getBackendUrl()}/tickets/${ticketId}/read`, {
				method: 'PUT',
				headers: {
					'Authorization': `Bearer ${localStorage.getItem('token')}`,
					'Content-Type': 'application/json'
				}
			});
			
			if (response.ok) {
				
				// ✅ ACTUALIZAR ESTADO LOCAL SOLO SI LA API RESPONDE OK
				setNotifications(prevNotifications => {
					const updated = prevNotifications.map(notification => {
						if (notification.ticketId === parseInt(ticketId)) {
							return { ...notification, isRead: true };
						}
						return notification;
					});
					return updated;
				});

				// ✅ ACTUALIZAR CONTADOR LOCAL SOLO SI LA API RESPONDE OK
				setNotificationCount(prevCount => Math.max(0, prevCount - 1));
				
				// ✅ RECARGAR DESDE API DESPUÉS DE UN PEQUEÑO DELAY PARA SINCRONIZAR
				setTimeout(async () => {
					await fetchNotifications();
				}, 1000);
			} else {
				console.error("❌ ERROR AL MARCAR TICKET COMO LEÍDO:", response.status);
				// ✅ NO REVERTIR CAMBIOS LOCALES - SIMPLEMENTE RECARGAR DESDE API
				await fetchNotifications();
			}
		} catch (error) {
			console.error("❌ ERROR AL MARCAR TICKET COMO LEÍDO:", error);
			// ✅ NO REVERTIR CAMBIOS LOCALES - SIMPLEMENTE RECARGAR DESDE API
			await fetchNotifications();
		}
	}, [fetchNotifications, getBackendUrl]);

	// ✅ FUNCIÓN ELIMINADA: updateConnectionStatusIndicator
	// El indicador de WhatsApp ahora está en WhatsAppIndicator component
	// en la barra superior para mejorar UX y no obstruir el botón AUDIO

	// ✅ FUNCIÓN DE DIAGNÓSTICO DE CONEXIONES WHATSAPP
	const checkWhatsAppConnections = useCallback(async () => {
		try {
			// ✅ USAR LA URL CORRECTA DEL BACKEND
			const response = await fetch(`${getBackendUrl()}/whatsapp/`, {
				method: 'GET',
				headers: {
					'Authorization': `Bearer ${localStorage.getItem('token')}`,
					'Content-Type': 'application/json'
				}
			});
			
			if (response.ok) {
				// ✅ VERIFICAR QUE LA RESPUESTA SEA JSON VÁLIDO
				const contentType = response.headers.get('content-type');
				if (!contentType || !contentType.includes('application/json')) {
				// console.warn("⚠️ RESPUESTA NO ES JSON, saltando verificación de conexiones");
					return { connected: 1, total: 1 }; // ✅ ASUMIR CONEXIÓN ACTIVA
				}
				
				const data = await response.json();
				const connectedCount = data.filter(w => w.status === 'CONNECTED').length;
				const totalCount = data.length;
				
				// ✅ INDICADOR MOVIDO A BARRA SUPERIOR (WhatsAppIndicator component)
				
				if (connectedCount === 0) {
					console.debug("⚠️ NO HAY CONEXIONES WHATSAPP ACTIVAS");
					// ✅ VERIFICAR SI YA EXISTE UNA NOTIFICACIÓN PARA EVITAR DUPLICADOS
					const existingNotification = document.querySelector('[data-whatsapp-notification]');
					if (existingNotification) {
						return { connected: connectedCount, total: totalCount };
					}
					
					// ✅ CREAR CONTENEDOR GLOBAL DE NOTIFICACIONES SI NO EXISTE
					let notificationContainer = document.getElementById('system-notifications-container');
					if (!notificationContainer) {
						notificationContainer = document.createElement('div');
						notificationContainer.id = 'system-notifications-container';
						notificationContainer.style.cssText = `
							position: fixed;
							top: 68px;
							right: 20px;
							z-index: 9999;
							display: flex;
							flex-direction: column;
							gap: 12px;
							max-width: 360px;
							pointer-events: none;
							align-items: flex-end;
						`;
						document.body.appendChild(notificationContainer);
					}
					
					// ✅ MOSTRAR NOTIFICACIÓN INFORMATIVA CON BOTÓN DE RECONEXIÓN - DISEÑO MEJORADO Y COMPACTO
					const notification = document.createElement('div');
					notification.setAttribute('data-whatsapp-notification', 'true');
					notification.setAttribute('data-notification-item', 'true');
					notification.style.cssText = `
						background: linear-gradient(135deg, #f44336 0%, #d32f2f 100%);
						color: white;
						padding: 0;
						border-radius: 10px;
						box-shadow: 0 6px 20px rgba(244, 67, 54, 0.4), 0 3px 6px rgba(0, 0, 0, 0.2);
						font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
						font-size: 13px;
						max-width: 360px;
						min-width: 280px;
						overflow: hidden;
						animation: slideInRight 0.3s ease-out;
						pointer-events: auto;
					`;
					
					// ✅ AGREGAR ANIMACIÓN CSS
					if (!document.getElementById('notification-animation-style')) {
						const style = document.createElement('style');
						style.id = 'notification-animation-style';
						style.textContent = `
							@keyframes slideInRight {
								from {
									transform: translateX(100%);
									opacity: 0;
								}
								to {
									transform: translateX(0);
									opacity: 1;
								}
							}
							@keyframes fadeOut {
								from {
									opacity: 1;
									transform: translateX(0);
								}
								to {
									opacity: 0;
									transform: translateX(100%);
								}
							}
						`;
						document.head.appendChild(style);
					}
					
					// ✅ DECLARAR timeoutId ANTES DE LA FUNCIÓN
					let timeoutId = null;
					
					// ✅ FUNCIÓN PARA CERRAR LA NOTIFICACIÓN CON ANIMACIÓN Y REORGANIZAR
					const closeNotification = () => {
						if (notification.parentNode) {
							notification.style.animation = 'fadeOut 0.3s ease-out';
							setTimeout(() => {
								if (notification.parentNode) {
									notification.parentNode.removeChild(notification);
									// ✅ REORGANIZAR NOTIFICACIONES RESTANTES
									reorganizeNotifications();
								}
							}, 300);
						}
						if (timeoutId) {
							clearTimeout(timeoutId);
							timeoutId = null;
						}
					};
					
					// ✅ FUNCIÓN PARA REORGANIZAR NOTIFICACIONES DESPUÉS DE CERRAR UNA
					const reorganizeNotifications = () => {
						const container = document.getElementById('system-notifications-container');
						if (!container) return;
						
						// ✅ ELIMINAR CONTENEDOR SI NO HAY NOTIFICACIONES
						const notifications = container.querySelectorAll('[data-notification-item]');
						if (notifications.length === 0) {
							container.remove();
						}
					};
					
					notification.innerHTML = `
						<div style="padding: 14px; position: relative;">
							<!-- Botón de cerrar -->
							<button id="close-notification-btn" style="
								position: absolute;
								top: 8px;
								right: 8px;
								background: rgba(255, 255, 255, 0.2);
								color: white;
								border: none;
								padding: 4px;
								border-radius: 50%;
								cursor: pointer;
								font-size: 16px;
								line-height: 1;
								width: 24px;
								height: 24px;
								display: flex;
								align-items: center;
								justify-content: center;
								transition: all 0.2s ease;
								font-weight: bold;
							" 
							onmouseover="this.style.background='rgba(255, 255, 255, 0.3)'; this.style.transform='scale(1.1)'"
							onmouseout="this.style.background='rgba(255, 255, 255, 0.2)'; this.style.transform='scale(1)'"
							title="Cerrar">
								×
							</button>
							
							<!-- Contenido principal -->
							<div style="display: flex; align-items: flex-start; gap: 12px; padding-right: 32px;">
								<!-- Icono -->
								<div style="
									background: rgba(255, 255, 255, 0.2);
									border-radius: 50%;
									width: 36px;
									height: 36px;
									display: flex;
									align-items: center;
									justify-content: center;
									font-size: 18px;
									flex-shrink: 0;
								">
									⚠️
								</div>
								
								<!-- Texto y botón -->
								<div style="flex: 1;">
									<div style="
										font-size: 14px;
										font-weight: 600;
										margin-bottom: 6px;
										line-height: 1.3;
									">
										Sin conexiones WhatsApp
									</div>
									<div style="
										font-size: 12px;
										opacity: 0.9;
										margin-bottom: 12px;
										line-height: 1.4;
									">
										No hay conexiones activas. Configura o reconecta tus conexiones WhatsApp para continuar.
									</div>
									<button id="reconnect-btn" style="
										background: white;
										color: #f44336;
										border: none;
										padding: 8px 16px;
										border-radius: 6px;
										cursor: pointer;
										font-weight: 600;
										font-size: 12px;
										width: 100%;
										transition: all 0.2s ease;
										box-shadow: 0 2px 6px rgba(0, 0, 0, 0.15);
										display: flex;
										align-items: center;
										justify-content: center;
										gap: 6px;
									"
									onmouseover="this.style.background='#f5f5f5'; this.style.transform='translateY(-1px)'; this.style.boxShadow='0 3px 10px rgba(0, 0, 0, 0.2)'"
									onmouseout="this.style.background='white'; this.style.transform='translateY(0)'; this.style.boxShadow='0 2px 6px rgba(0, 0, 0, 0.15)'">
										<span>🔄</span>
										<span>RECONECTAR AHORA</span>
									</button>
								</div>
							</div>
						</div>
					`;
					
					notificationContainer.appendChild(notification);
					
					// ✅ AGREGAR EVENTO AL BOTÓN DE CERRAR
					const closeBtn = notification.querySelector('#close-notification-btn');
					if (closeBtn) {
						closeBtn.onclick = (e) => {
							e.stopPropagation();
							closeNotification();
						};
					}
					
					// ✅ AGREGAR EVENTO AL BOTÓN DE RECONECTAR
					const reconnectBtn = notification.querySelector('#reconnect-btn');
					if (reconnectBtn) {
						reconnectBtn.onclick = (e) => {
							e.stopPropagation();
							window.location.href = '/connections';
							closeNotification();
						};
					}
					
					// ✅ REDUCIR TIEMPO DE AUTO-CIERRE A 5 SEGUNDOS
					timeoutId = setTimeout(() => {
						closeNotification();
					}, 5000);
				}
				
				return { connected: connectedCount, total: totalCount };
			} else {
				// console.warn("⚠️ ERROR AL VERIFICAR CONEXIONES:", response.status, "- Asumiendo conexión activa");
				return { connected: 1, total: 1 }; // ✅ ASUMIR CONEXIÓN ACTIVA EN CASO DE ERROR
			}
		} catch (error) {
			// console.warn("⚠️ ERROR AL VERIFICAR CONEXIONES WHATSAPP:", error.message, "- Asumiendo conexión activa");
			return { connected: 1, total: 1 }; // ✅ ASUMIR CONEXIÓN ACTIVA EN CASO DE ERROR
		}
	}, [getBackendUrl]);


	// ✅ USAR SOCKET CONTEXT DIRECTAMENTE (COMO TicketsListCustom)
	const socketManager = useContext(SocketContext);

	// ✅ PREVENIR RE-MONTADOS Y CONFIGURAR NOTIFICACIONES NATIVAS
	useEffect(() => {
		if (componentInitializedRef.current) return;
		
		isMountedRef.current = true;
		componentInitializedRef.current = true;
		
		console.log("🔔 NotificationsPopOver: Inicialización única del componente");
		
		// ✅ CONFIGURAR SONIDO - Ya está configurado en el useEffect anterior con playSound
		// soundAlertRef.current ya está configurado con playSound en el useEffect de inicialización
		
		// ✅ NO SOLICITAR PERMISOS AUTOMÁTICAMENTE - Solo cuando el usuario interactúe
		// Los permisos se solicitarán cuando el usuario haga clic en el botón de notificaciones
		
		return () => {
			isMountedRef.current = false;
			componentInitializedRef.current = false;
		};
	}, []); // ✅ CORRECCIÓN: Remover dependencia de 'play' que ya no existe

	// ✅ HANDLER PARA ACTUALIZAR CONTADOR DE MENSAJES NO LEÍDOS
	const handleTicketUpdateUnread = useCallback((data) => {
		
		if (!isMountedRef.current) {
			return;
		}

		if (data.action === "updateUnread" && data.ticketId) {
			
			// ✅ ACTUALIZAR NOTIFICACIONES LOCALES
			setNotifications(prevNotifications => {
				const updated = prevNotifications.map(notification => {
					if (notification.ticketId === data.ticketId) {
						return { ...notification, isRead: true };
					}
					return notification;
				});
				return updated;
			});
			
			// ✅ ACTUALIZAR CONTADOR LOCAL
			setNotificationCount(prevCount => Math.max(0, prevCount - 1));
			
			// ✅ NO RECARGAR DESDE API - MANTENER ESTADO LOCAL
			// El estado local ya está actualizado correctamente
		}
	}, []); // ✅ REMOVER fetchNotifications PARA EVITAR WARNING

	// ✅ HANDLERS ESTABLES CON useCallback PARA EVITAR RE-CREACIONES
	const handleAppMessage = useCallback(async (data) => {
		// ✅ LOGS REMOVIDOS PARA USUARIO FINAL
		
		// ✅ VERIFICAR QUE EL COMPONENTE ESTÉ MONTADO
		if (!isMountedRef.current) {
			// ✅ LOGS REMOVIDOS PARA USUARIO FINAL
			return;
		}
		
		if (!data || typeof data !== 'object') {
			// ✅ LOGS REMOVIDOS PARA USUARIO FINAL
			return;
		}
		
		if (data.action === "create") {
			// ✅ LOGS REMOVIDOS PARA USUARIO FINAL
			
			// ✅ FILTRAR POR USUARIO: Si el usuario no es admin, solo procesar notificaciones de tickets asignados a él
			if (user?.profile !== 'admin' && data.ticket?.userId !== user?.id) {
				// ✅ Usuario regular y el ticket no está asignado a él - ignorar notificación
				return;
			}
			
			// ✅ CORRECCIÓN #1: Determinar si debemos reproducir sonido
			// Reproducir sonido si:
			// 1. Es un mensaje entrante (fromMe: false) - mensaje del usuario
			// 2. O es un mensaje del bot/Typebot (fromMe: true) pero el ticket tiene integración activa
			const isIncomingMessage = !data.message?.fromMe;
			const isBotMessageWithIntegration = data.message?.fromMe && (data.ticket?.useIntegration || data.ticket?.integrationId || data.ticket?.promptId);
			const shouldPlaySound = isIncomingMessage || isBotMessageWithIntegration;
			
			// ✅ PREVENIR DUPLICADOS - Solo agregar si no es del usuario actual (mensajes enviados manualmente)
			// Pero permitir mensajes de bots/IA para mostrar notificaciones
			if (data.message?.fromMe && !isBotMessageWithIntegration) {
				// ✅ LOGS REMOVIDOS PARA USUARIO FINAL
				return;
			}
				
			// ✅ CORRECCIÓN #1: REPRODUCIR SONIDO AUTOMÁTICO - Solo si corresponde
			if (shouldPlaySound) {
				try {
					if (soundAlertRef.current && typeof soundAlertRef.current === 'function') {
						soundAlertRef.current();
					}
				} catch (error) {
					console.error("🔔 ERROR AL REPRODUCIR SONIDO:", error);
				}
			}
			
			// ✅ NOTIFICACIÓN NATIVA DEL SISTEMA
			if ("Notification" in window && Notification.permission === "granted") {
				try {
					new Notification(`Nuevo mensaje de ${data.contact?.name || 'Contacto'}`, {
						body: data.message?.body || 'Nuevo mensaje',
						icon: faviconUrl, // ✅ Usar URL dinámica del favicon
						tag: `ticket-${data.ticket?.id}`,
						requireInteraction: false
					});
				} catch (error) {
					console.error("🔔 ERROR AL MOSTRAR NOTIFICACIÓN NATIVA:", error);
				}
			}
				
			// ✅ ACTUALIZACIÓN LOCAL INMEDIATA - EVITAR DUPLICADOS
			// Primero actualizar localmente para respuesta inmediata
			const newNotification = {
				id: `ticket-${data.ticket?.id}`, // ✅ ID ÚNICO POR TICKET (NO POR MENSAJE)
				ticketId: data.ticket?.id,
				contactId: data.contact?.id || data.ticket?.contactId,
				message: data.message?.body || 'Nuevo mensaje',
				ticketStatus: data.ticket?.status || 'open',
				queueName: data.ticket?.queue?.name || 'Sin departamento',
				tags: data.ticket?.tags || [],
				contactName: data.contact?.name || 'Contacto',
				contactAvatar: data.contact?.avatar || null,
				timestamp: new Date(),
				metadata: {
					ticketUuid: data.ticket?.uuid,
					contactName: data.contact?.name || 'Contacto',
					contactAvatar: data.contact?.avatar || null,
					messageId: data.message?.id, // ✅ ID DEL MENSAJE PARA IDENTIFICACIÓN ÚNICA
					lastMessage: data.message?.body || 'Nuevo mensaje' // ✅ ÚLTIMO MENSAJE
				},
				createdAt: new Date(),
				isRead: false
			};

			// ✅ ACTUALIZAR ESTADO LOCAL INMEDIATAMENTE - EVITAR DUPLICADOS
			setNotifications(prevNotifications => {
				// ✅ LOGS REMOVIDOS PARA USUARIO FINAL
				
				// ✅ EVITAR DUPLICADOS: Buscar si ya existe notificación para este ticket
				const existingIndex = prevNotifications.findIndex(n => n.ticketId === data.ticket?.id);
				
				if (existingIndex >= 0) {
					// ✅ ACTUALIZAR NOTIFICACIÓN EXISTENTE
					const updated = [...prevNotifications];
					updated[existingIndex] = {
						...updated[existingIndex],
						message: data.message?.body || 'Nuevo mensaje',
						timestamp: new Date(),
						metadata: {
							...updated[existingIndex].metadata,
							messageId: data.message?.id,
							lastMessage: data.message?.body || 'Nuevo mensaje'
						},
						isRead: false // ✅ MARCAR COMO NO LEÍDO
					};
					
					// ✅ LOGS REMOVIDOS PARA USUARIO FINAL
					
					return updated;
				} else {
					// ✅ AGREGAR NUEVA NOTIFICACIÓN
					const newState = [newNotification, ...prevNotifications];
					// Logs de depuración removidos
					return newState;
				}
			});

			// ✅ NO ACTUALIZAR CONTADOR MANUALMENTE - EL useEffect LO HARÁ

			// ✅ NO RECARGAR DESDE API - MANTENER ESTADO LOCAL
			// El estado local ya está actualizado correctamente
		}
	}, [faviconUrl, user]); // ✅ Incluir faviconUrl y user en dependencias


	// ✅ CONFIGURAR LISTENERS CON SOCKET DIRECTO - OPTIMIZADO PARA EVITAR DUPLICADOS
	useEffect(() => {
		if (!componentInitializedRef.current || socketInitializedRef.current || !isMountedRef.current) {
			return;
		}
		
		const companyId = localStorage.getItem("companyId");
		const socket = socketManager.getSocket(companyId);
		
		if (!socket || !socket.socket) return;
		
		socketInitializedRef.current = true;

		// ✅ CORRECCIÓN #4: Función para suscribirse a notificaciones
		const subscribeToNotifications = () => {
			if (!isMountedRef.current) return;
			socket.emit("joinNotification");
		};

		// ✅ MANEJAR RECONEXIÓN DE SOCKET
		const handleConnect = () => {
			if (!isMountedRef.current) return;
			// Logs de depuración removidos
			subscribeToNotifications();
		};

		const handleDisconnect = (reason) => {
			if (!isMountedRef.current) return;
			// Logs de depuración removidos
		};

		// ✅ CORRECCIÓN #4: Suscribirse cuando el socket está ready
		const handleReady = () => {
			if (!isMountedRef.current) return;
			// Logs de depuración removidos
			subscribeToNotifications();
		};

		// ✅ LIMPIAR LISTENERS ANTERIORES PARA EVITAR DUPLICADOS
		socket.off("connect", handleConnect);
		socket.off("disconnect", handleDisconnect);
		socket.off("ready", handleReady);
		socket.off(`company-${companyId}-appMessage`, handleAppMessage);
		socket.off(`company-${companyId}-ticket`, handleTicketUpdateUnread);

		// ✅ CONFIGURAR LISTENERS PRINCIPALES
		socket.on("connect", handleConnect);
		socket.on("disconnect", handleDisconnect);
		socket.on("ready", handleReady);
		socket.on(`company-${companyId}-appMessage`, handleAppMessage);
		socket.on(`company-${companyId}-ticket`, handleTicketUpdateUnread);

		// ✅ CORRECCIÓN #4: Intentar suscripción inmediata (si el socket ya está conectado)
		// Si no está conectado, el listener de "ready" o "connect" se encargará
		if (socket.socket && socket.socket.connected) {
			subscribeToNotifications();
		}

		return () => {
			if (!isMountedRef.current) return;
			socketInitializedRef.current = false;
			socket.off("connect", handleConnect);
			socket.off("disconnect", handleDisconnect);
			socket.off("ready", handleReady);
			socket.off(`company-${companyId}-appMessage`, handleAppMessage);
			socket.off(`company-${companyId}-ticket`, handleTicketUpdateUnread);
		};
	}, [socketManager, handleAppMessage, handleTicketUpdateUnread]); // ✅ SIMPLIFICAR DEPENDENCIAS

	// ✅ ELIMINADO: useEffect de localStorage - Ahora se maneja desde la API

	// ✅ CARGAR NOTIFICACIONES AL MONTAR EL COMPONENTE
	useEffect(() => {
		if (componentInitializedRef.current) {
			fetchNotifications();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []); // ✅ REMOVER fetchNotifications PARA EVITAR WARNING

	// ✅ VERIFICAR CONEXIONES WHATSAPP AL MONTAR EL COMPONENTE
	useEffect(() => {
		// ✅ VERIFICAR CONEXIONES DESPUÉS DE UN PEQUEÑO DELAY
		const timer = setTimeout(() => {
			checkWhatsAppConnections();
		}, 2000);
		
		return () => clearTimeout(timer);
	}, [checkWhatsAppConnections]); // ✅ INCLUIR checkWhatsAppConnections PARA QUE FUNCIONE

	// ✅ SINCRONIZAR CONTADOR CON NOTIFICACIONES
	useEffect(() => {
		const unreadCount = notifications.filter(n => !n.isRead).length;
		setNotificationCount(unreadCount);
	}, [notifications]); // ✅ SINCRONIZAR CUANDO CAMBIEN LAS NOTIFICACIONES

	// ✅ NAVEGAR AL TICKET ESPECÍFICO CON VALIDACIÓN DE ESTADO
	const goToTicket = async (notification) => {
		
		// ✅ VALIDACIÓN: NO PERMITIR ABRIR TICKETS PENDING
		if (notification.ticketStatus === 'pending') {
			// ✅ MOSTRAR MENSAJE INFORMATIVO MÁS ELEGANTE
			console.warn("⚠️ ACCIÓN BLOQUEADA: Este ticket está pendiente. Debes aceptarlo o finalizarlo primero.");
			// ✅ CREAR NOTIFICACIÓN TEMPORAL EN LA UI
			const tempNotification = document.createElement('div');
			tempNotification.style.cssText = `
				position: fixed;
				top: 20px;
				right: 20px;
				background: #ff9800;
				color: white;
				padding: 12px 16px;
				border-radius: 8px;
				box-shadow: 0 4px 12px rgba(0,0,0,0.3);
				z-index: 9999;
				font-family: Arial, sans-serif;
				font-size: 14px;
				max-width: 300px;
			`;
			tempNotification.innerHTML = '⚠️ <strong>Ticket Pendiente</strong><br/>Debes aceptarlo o finalizarlo primero';
			document.body.appendChild(tempNotification);
			
			// ✅ REMOVER DESPUÉS DE 4 SEGUNDOS
			setTimeout(() => {
				if (tempNotification.parentNode) {
					tempNotification.parentNode.removeChild(tempNotification);
				}
			}, 4000);
			
			return;
		}
		
		// ✅ PERMITIR NAVEGACIÓN PARA TICKETS OPEN
		if (notification.ticketStatus === 'open') {
			// ✅ MARCAR COMO LEÍDA AL NAVEGAR
			await markNotificationAsRead(notification.id);
			// ✅ USAR UUID PARA NAVEGACIÓN CORRECTA
			if (notification.metadata?.ticketUuid) {
				window.location.replace(`/tickets/${notification.metadata.ticketUuid}`);
			} else {
				console.error("❌ ERROR: No se encontró UUID para el ticket:", notification.ticketId);
				window.location.replace(`/tickets/${notification.ticketId}`);
			}
			return;
		}
		
		// ✅ OTROS ESTADOS (closed, etc.) - NAVEGACIÓN NORMAL
		await markNotificationAsRead(notification.id);
		// ✅ USAR UUID PARA NAVEGACIÓN CORRECTA
		if (notification.metadata?.ticketUuid) {
			window.location.replace(`/tickets/${notification.metadata.ticketUuid}`);
		} else {
			console.error("❌ ERROR: No se encontró UUID para el ticket:", notification.ticketId);
			window.location.replace(`/tickets/${notification.ticketId}`);
		}
	};

	// ✅ MARCAR NOTIFICACIÓN COMO LEÍDA - FUNCIÓN NO UTILIZADA ACTUALMENTE
	// const markAsRead = (notificationId) => {
	// 	setNotifications(prev => {
	// 		const updated = prev.map(notification => {
	// 			if (notification.id === notificationId) {
	// 				return { ...notification, isRead: true };
	// 			}
	// 			return notification;
	// 		});
	// 		
	// 		// ✅ ACTUALIZAR CONTADOR - SOLO CONTAR NO LEÍDAS
	// 		const unreadCount = updated.filter(n => !n.isRead).length;
	// 		setNotificationCount(unreadCount);
	// 		
	// 		console.log("🔔 NOTIFICACIÓN MARCADA COMO LEÍDA:", notificationId);
	// 		console.log("🔔 CONTADOR ACTUALIZADO:", unreadCount);
	// 		
	// 		return updated;
	// 	});
	// };

	// ✅ ACEPTAR TICKET
	const acceptTicket = async (notification) => {
		try {
			// ✅ LLAMAR A LA API PARA ACEPTAR EL TICKET
			const api = await import("../../services/api");
			await api.default.put(`/tickets/${notification.ticketId}/accept`);
			
			// ✅ MARCAR COMO LEÍDA Y ACTUALIZAR ESTADO
			await markNotificationAsRead(notification.id);
			
			// ✅ ACTUALIZAR ESTADO DE LA NOTIFICACIÓN PARA GRUPOS
			setNotifications(prev => {
				return prev.map(n => {
					if (n.id === notification.id) {
						return { 
							...n, 
							ticketStatus: 'open', 
							isRead: true 
						};
					}
					return n;
				});
			});
			
			// ✅ NAVEGAR AL TICKET DESPUÉS DE ACEPTAR
			setTimeout(() => {
				// ✅ USAR UUID PARA NAVEGACIÓN CORRECTA
				if (notification.metadata?.ticketUuid) {
					window.location.replace(`/tickets/${notification.metadata.ticketUuid}`);
				} else {
					console.error("❌ ERROR: No se encontró UUID para el ticket aceptado:", notification.ticketId);
					window.location.replace(`/tickets/${notification.ticketId}`);
				}
			}, 500); // ✅ PEQUEÑO DELAY PARA QUE SE ACTUALICE EL ESTADO
			
		} catch (error) {
			console.error("🔔 ERROR AL ACEPTAR TICKET:", error);
		}
	};

	// ✅ FINALIZAR TICKET
	const finalizeTicket = async (notification) => {
		try {
			// ✅ LLAMAR A LA API PARA FINALIZAR EL TICKET
			const api = await import("../../services/api");
			await api.default.put(`/tickets/${notification.ticketId}/close`);
			
			// ✅ MARCAR COMO LEÍDA Y ACTUALIZAR ESTADO
			await markNotificationAsRead(notification.id);
			
			// ✅ ACTUALIZAR ESTADO DEL TICKET EN LA NOTIFICACIÓN
			setNotifications(prev => {
				return prev.map(n => {
					if (n.id === notification.id) {
						return { ...n, ticketStatus: 'closed', isRead: true };
					}
					return n;
				});
			});
			
		} catch (error) {
			console.error("🔔 ERROR AL FINALIZAR TICKET:", error);
		}
	};

	// ✅ MANEJAR CLIC EN EL ICONO
	const handleClick = (event) => {
		// ✅ PREVENIR PROPAGACIÓN DEL EVENTO AL ELEMENTO PADRE
		event.stopPropagation();
		
		// ✅ SOLICITAR PERMISOS DE NOTIFICACIÓN SOLO AL HACER CLIC
		if ("Notification" in window && Notification.permission === "default") {
			Notification.requestPermission().then(permission => {
				if (permission === "granted") {
					// Logs de depuración removidos
				}
			});
		}
		
		// ✅ REPRODUCIR SONIDO AL HACER CLIC
		if (soundAlertRef.current) {
			soundAlertRef.current();
		}
		
		setAnchorEl(event.currentTarget);
	};

	// ✅ CERRAR POPOVER
	const handleClose = (event, reason) => {
		// ✅ PREVENIR PROPAGACIÓN DEL EVENTO AL CERRAR
		if (event) {
			event.stopPropagation();
		}
		setAnchorEl(null);
		// ✅ NO RESETEAR CONTADOR AL CERRAR - MANTENER NOTIFICACIONES
		// setNotificationCount(0);
	};

	// ✅ OBTENER COLOR DE ESTADO DEL TICKET
	const getStatusColor = (status) => {
		switch (status) {
			case 'open': return '#4caf50'; // Verde
			case 'pending': return '#ff9800'; // Naranja
			case 'closed': return '#f44336'; // Rojo
			default: return '#9e9e9e'; // Gris
		}
	};

	// ✅ OBTENER CURSOR SEGÚN ESTADO
	const getCursorStyle = (status) => {
		switch (status) {
			case 'pending': return 'not-allowed';
			case 'open': return 'pointer';
			case 'closed': return 'pointer';
			default: return 'default';
		}
	};

	// ✅ OBTENER TOOLTIP SEGÚN ESTADO
	const getTooltipText = (status) => {
		switch (status) {
			case 'pending': return '⚠️ Ticket pendiente - Debes aceptarlo o finalizarlo primero';
			case 'open': return '✅ Click para abrir el ticket';
			case 'closed': return '📋 Click para ver el ticket cerrado';
			default: return 'Click para ver el ticket';
		}
	};

	// ✅ FORMATEAR TIEMPO TRANSCURRIDO
	const formatTimeAgo = (timestamp) => {
		const now = new Date();
		const diff = now - timestamp;
		const minutes = Math.floor(diff / 60000);
		const hours = Math.floor(diff / 3600000);
		const days = Math.floor(diff / 86400000);

		if (days > 0) return `${days} día${days > 1 ? 's' : ''} atrás`;
		if (hours > 0) return `${hours} hora${hours > 1 ? 's' : ''} atrás`;
		if (minutes > 0) return `${minutes} min atrás`;
		return 'Ahora';
	};
	
	return (
		<div>
			<IconButton 
				color="inherit" 
				onClick={handleClick}
				onMouseDown={(e) => {
					// ✅ PREVENIR PROPAGACIÓN TAMBIÉN EN MOUSEDOWN
					e.stopPropagation();
				}}
				size="small"
				style={customStyle || {}}
				onMouseEnter={(e) => {
					if (customStyle) {
						e.currentTarget.style.color = theme.palette.primary.main;
						e.currentTarget.style.backgroundColor = theme.mode === 'dark' ? '#374151' : '#f5f5f5';
					}
				}}
				onMouseLeave={(e) => {
					if (customStyle) {
						e.currentTarget.style.color = theme.palette.text.secondary;
						e.currentTarget.style.backgroundColor = 'transparent';
					}
				}}
			>
				<Badge 
					badgeContent={notificationCount} 
					color="secondary"
					max={99}
					invisible={notificationCount === 0}
				>
					{customStyle ? (
						<WhatsAppIcon fontSize="small" style={{ 
							color: theme.palette.text.primary 
						}} />
					) : (
						<WhatsAppIcon style={{ color: 'white' }} />
					)}
				</Badge>
			</IconButton>
			
			<Popover
				open={Boolean(anchorEl)} // ✅ SOLO ABRIR AL HACER CLICK
				anchorEl={anchorEl}
				onClose={handleClose}
				anchorOrigin={{
					vertical: 'bottom',
					horizontal: 'right',
				}}
				transformOrigin={{
					vertical: 'top',
					horizontal: 'right',
				}}
				style={{
					zIndex: 1300, // ✅ Z-INDEX NORMAL DE MATERIAL-UI
				}}
				PaperProps={{
					style: {
						maxWidth: '400px',
						maxHeight: '80vh',
						overflowY: 'auto'
					}
				}}
			>
				<div style={{ 
					width: 400, 
					maxHeight: 500, 
					overflowY: 'auto',
					backgroundColor: theme.mode === 'dark' ? '#1f2937' : '#ffffff'
				}}>
					<div style={{ 
						padding: '16px 20px', 
						borderBottom: `1px solid ${theme.mode === 'dark' ? '#374151' : '#eee'}`,
						background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
						color: 'white',
						position: 'sticky',
						top: 0,
						zIndex: 1,
						display: 'flex',
						justifyContent: 'space-between',
						alignItems: 'flex-start'
					}}>
						<div style={{ flex: 1 }}>
							<Typography variant="h6" style={{ 
								marginBottom: 6, 
								fontWeight: 'bold',
								color: 'white',
								display: 'flex',
								alignItems: 'center',
								gap: 10,
								fontSize: '16px'
							}}>
								<span role="img" aria-label="bell" style={{ fontSize: '20px' }}>🔔</span> 
								<span>Notificaciones ({notificationCount})</span>
							</Typography>
							<Typography variant="body2" style={{ 
								color: 'rgba(255,255,255,0.9)',
								fontSize: '13px'
							}}>
								{notificationCount > 0 
									? `${notificationCount} mensaje${notificationCount !== 1 ? 's' : ''} nuevo${notificationCount !== 1 ? 's' : ''} de WhatsApp`
									: 'No hay mensajes nuevos'
								}
							</Typography>
						</div>
						<IconButton
							onClick={(e) => {
								e.stopPropagation();
								handleClose(e);
							}}
							size="small"
							style={{
								color: 'white',
								padding: '4px',
								marginTop: '-4px',
								marginRight: '-4px'
							}}
							onMouseEnter={(e) => {
								e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.2)';
							}}
							onMouseLeave={(e) => {
								e.currentTarget.style.backgroundColor = 'transparent';
							}}
						>
							<CloseIcon fontSize="small" />
						</IconButton>
					</div>
					
					{notifications.length === 0 ? (
						<div style={{ 
							padding: 40, 
							textAlign: 'center',
							background: theme.mode === 'dark' 
								? 'linear-gradient(135deg, #374151 0%, #1f2937 100%)' 
								: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
							margin: 16,
							borderRadius: 12
						}}>
							<div style={{ fontSize: 48, marginBottom: 16 }}>
								<span role="img" aria-label="empty">📭</span>
							</div>
							<Typography variant="h6" gutterBottom style={{ 
								color: theme.mode === 'dark' ? '#d1d5db' : '#666', 
								fontWeight: 600 
							}}>
								No hay notificaciones nuevas
							</Typography>
							<Typography variant="body2" style={{ 
								color: theme.mode === 'dark' ? '#9ca3af' : undefined 
							}}>
								Cuando recibas mensajes de WhatsApp aparecerán aquí
							</Typography>
						</div>
					) : (
						<List style={{ padding: 0 }}>
							{notifications.map((notification, index) => {
								return (
								<Tooltip 
									key={notification.id}
									title={getTooltipText(notification.ticketStatus)}
									placement="left"
								>
									<ListItem 
										button
										onClick={() => goToTicket(notification)}
										style={{
											background: 'white',
											borderBottom: `1px solid #eee`,
											cursor: getCursorStyle(notification.ticketStatus),
											padding: '14px 16px',
											transition: 'all 0.2s ease',
											margin: '0',
											position: 'relative',
											// ✅ ESTILO ESPECIAL PARA TICKETS PENDING
											opacity: notification.ticketStatus === 'pending' ? 0.95 : 1,
											borderLeft: `4px solid ${getStatusColor(notification.ticketStatus)}`,
											boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
										}}
										onMouseEnter={(e) => {
											if (notification.ticketStatus !== 'pending') {
												e.currentTarget.style.backgroundColor = '#f5f5f5';
												e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.15)';
											} else {
												e.currentTarget.style.backgroundColor = '#fff8e1';
												e.currentTarget.style.boxShadow = '0 2px 8px rgba(255,152,0,0.25)';
											}
										}}
										onMouseLeave={(e) => {
											e.currentTarget.style.backgroundColor = 'white';
											e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.1)';
										}}
									>
									<div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, width: '100%' }}>
										{/* Avatar con número de mensajes */}
										<div style={{ position: 'relative', flexShrink: 0 }}>
											{notification.contactAvatar ? (
												<img 
													src={notification.contactAvatar} 
													alt={notification.contactName}
													style={{ 
														width: 40, 
														height: 40, 
														borderRadius: '50%', 
														objectFit: 'cover',
														border: '2px solid #e0e0e0'
													}}
													onError={(e) => {
														e.target.style.display = 'none';
													}}
												/>
											) : (
												<div style={{
													width: 40,
													height: 40,
													borderRadius: '50%',
													background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
													color: 'white',
													display: 'flex',
													alignItems: 'center',
													justifyContent: 'center',
													fontSize: '16px',
													fontWeight: 'bold',
													border: '2px solid #e0e0e0'
												}}>
													{(notification.contactName || 'C').charAt(0).toUpperCase()}
												</div>
											)}
											{/* Badge de número de mensajes */}
											<div style={{
												position: 'absolute',
												bottom: -2,
												right: -2,
												width: 20,
												height: 20,
												borderRadius: '50%',
												background: '#2196f3',
												color: 'white',
												display: 'flex',
												alignItems: 'center',
												justifyContent: 'center',
												fontSize: '11px',
												fontWeight: 'bold',
												border: '2px solid white',
												boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
											}}>
												1
											</div>
										</div>
										
										{/* Contenido principal */}
										<div style={{ flex: 1, minWidth: 0 }}>
											{/* Fila superior: Nombre y tiempo */}
											<div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
												<Typography variant="subtitle2" style={{ 
													fontWeight: 'bold', 
													fontSize: '14px',
													color: '#000',
													overflow: 'hidden',
													textOverflow: 'ellipsis',
													whiteSpace: 'nowrap'
												}}>
													{notification.contactName || 'Contacto'}
												</Typography>
												<Typography variant="caption" style={{ 
													fontSize: '12px',
													color: '#666',
													marginLeft: 8,
													flexShrink: 0
												}}>
													{formatTimeAgo(notification.timestamp)}
												</Typography>
											</div>
											
											{/* Mensaje */}
											<Typography variant="body2" style={{ 
												marginBottom: 8,
												fontSize: '13px',
												color: '#333',
												lineHeight: 1.4
											}}>
												{notification.message.length > 50 
													? `${notification.message.substring(0, 50)}...` 
													: notification.message
												}
											</Typography>
											
											{/* Fila inferior: Ticket info y acciones */}
											<div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
												<div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
													<Typography variant="caption" style={{ 
														fontSize: '11px',
														color: '#999'
													}}>
														Ticket #{notification.ticketId}
													</Typography>
													{notification.queueName && (
														<>
															<span style={{ color: '#ccc', fontSize: '10px' }}>•</span>
															<Typography variant="caption" style={{ 
																fontSize: '11px',
																color: '#999'
															}}>
																{notification.queueName}
															</Typography>
														</>
													)}
												</div>
												
												{/* Badge de estado y acciones */}
												<div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
													{notification.ticketStatus === 'pending' && (
														<div style={{ 
															backgroundColor: '#ff9800',
															color: 'white',
															padding: '3px 8px',
															borderRadius: '12px',
															fontSize: '10px',
															fontWeight: 'bold',
															textTransform: 'uppercase',
															letterSpacing: '0.5px'
														}}>
															PENDING
														</div>
													)}
													
													{/* ✅ ICONOS DE ACCIÓN PARA TICKETS PENDIENTE */}
													{notification.ticketStatus === 'pending' && (
														<div style={{ display: 'flex', gap: 4 }}>
															<Tooltip title="Aceptar ticket">
																<IconButton
																	size="small"
																	onClick={(e) => {
																		e.stopPropagation();
																		acceptTicket(notification);
																	}}
																	style={{ 
																		color: '#4caf50',
																		padding: '6px',
																		backgroundColor: 'rgba(76, 175, 80, 0.1)',
																		width: 28,
																		height: 28
																	}}
																>
																	<CheckIcon style={{ fontSize: '16px' }} />
																</IconButton>
															</Tooltip>
															
															<Tooltip title="Finalizar ticket">
																<IconButton
																	size="small"
																	onClick={(e) => {
																		e.stopPropagation();
																		finalizeTicket(notification);
																	}}
																	style={{ 
																		color: '#f44336',
																		padding: '6px',
																		backgroundColor: 'rgba(244, 67, 54, 0.1)',
																		width: 28,
																		height: 28
																	}}
																>
																	<CloseIcon style={{ fontSize: '16px' }} />
																</IconButton>
															</Tooltip>
														</div>
													)}
												</div>
											</div>
										</div>
									</div>
								</ListItem>
							</Tooltip>
							);
						})}
						</List>
					)}
				</div>
			</Popover>
		</div>
	);
};

export default NotificationsPopOver;